import type * as pf from '@amirada/lib-shared'
import { NodeType } from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import { useAppIndexStore } from '@base/stores/indexStore'
import { useMaskStore } from '@base/stores/maskStore'
import { EFilterType } from '@base/utils/constants'
import { ut_buildParamData, ut_getFilterDescr, ut_getFilterNodes, ut_getSelectedFromPsx, ut_navigateNewRoute } from '@base/utils/utils'

export interface IMaskProps {
  // A node OUTSIDE the current navigation. Set → nobody has loaded it, so this composable fetches it
  // (see the watcher below). Leave it unset for the navigated node: passing paramData.namePath here
  // would freeze the path at setup time instead of following the route.
  objPath?: string
  // No navigation, no url change: the filter lives in the psx prop and picking one emits instead of
  // navigating. Also means the navigation loaded the mask for the URL's psx, not for this one.
  noUrlParams?: boolean
  psx?: string
  // Product-set narrowing (node-id whitelist, see IParamQueryUrlServer.psn). Must be threaded through
  // like psx: it is part of the maskStore cache key, so reading without it would miss the entry the
  // caller stored.
  psn?: number[]
}

export function useMask(p: IMaskProps = {}, emit?: (e: 'update:psx', v: string) => void) {
  const indexStore = useAppIndexStore()
  const maskStore = useMaskStore()

  /* onMounted(async() => {
    console.log('mask onMounted')
  })

  onBeforeMount(async() => {
    console.log('mask onBeforeMount')
  })

  onUpdated(async() => {
    console.log('mask onUpdated')
  })

  onUnmounted(async() => {
    console.log('mask onUnmounted')
  }) */

  const selected: Ref<string[]> = ref([]) // selected values

  // The paramData this composable reads and fetches under. One function for both, so the cache key
  // is computed in a single place.
  function buildParamData(): tp.IParamData {
    // noUrlParams (modal): read the mask for the local psx so hit counts / dependent filters reflect the selection.
    return ut_buildParamData(indexStore.state.paramData, p.noUrlParams
      ? { objPath: p.objPath, psx: p.psx, psn: p.psn }
      : { objPath: p.objPath })
  }

  // Fetch what the navigation did not. Two ways to end up on a key it never loaded: objPath asks for
  // another node, noUrlParams asks for another filter than the URL's. Neither set → no fetch here at
  // all; the navigation has the data and a second loader could only duplicate its request.
  // Gated inside rather than around the watcher: both are props and may arrive after setup.
  async function load(): Promise<void> {
    if (!p.objPath && !p.noUrlParams) { return }
    await maskStore.callMaskData(buildParamData())
  }
  watch([() => p.objPath, () => p.psx, () => p.psn], () => { void load() }, { immediate: true })

  // noUrlParams: the consumer switches the key itself, right away — unlike a navigation, where
  // paramData only advances once the BATCH has returned (indexStore, setParamDataMutation) and
  // everything is exchanged at once. This holds the previous mask until the new one is there, so a
  // locally filtered mask behaves like the rest of the app instead of blanking in between.
  // A consumer that WANTS it gone takes the component off the page — QuickSelect does that on a new
  // duty point, and this ref dies with it.
  const lastMaskData: Ref<pf.MaskNode | undefined> = ref(undefined)

  const maskData = computed((): pf.MaskNode | undefined => {
    const data: pf.MaskNode | undefined = maskStore.getMaskData(buildParamData())
    const res: pf.MaskNode | undefined = data || (p.noUrlParams ? lastMaskData.value : undefined)
    if (!res) { return }
    // console.log('compute maskData', res.desc);

    /*
     * The selection, as the MASK knows it — every item the mask cannot name is dropped here.
     *
     * A psx may carry a filter or a value that does not exist (a typo, an old bookmark, a value that
     * has since gone). The SERVER already ignores such an item: the result comes back unnarrowed, it
     * only echoes the psx in list.meta and marks nothing as selected. Without this filter the client
     * claimed otherwise — the chip row rendered a button with an EMPTY label, an x for something
     * invisible, and it counted towards "reset all" as if a filter were active.
     *
     * ⚑ Dropped from the SELECTION, not from the url: the address stays as it was written until
     * somebody touches a filter (setPsx then writes the selection back, without it). Rewriting an
     * address unasked would take a decision away from whoever built that link.
     *
     * ut_getFilterDescr is the test because it is the same question the display asks: it answers ''
     * for an unknown filter node and for an unknown value, and it describes a slider from its range
     * without needing a value at all.
     */
    const psx: string = p.noUrlParams ? (p.psx || '') : (indexStore.state.paramData.queryUrlServer.psx || '')
    const filterNodes: pf.TPfNode[] = ut_getFilterNodes(res)
    selected.value = ut_getSelectedFromPsx(psx).filter(item => !!ut_getFilterDescr(item, filterNodes))

    return res
  })

  // Gated inside, not around the watcher: noUrlParams is a prop and may arrive after setup.
  watch(maskData, (v) => {
    if (v && p.noUrlParams) { lastMaskData.value = v }
  })

  // get filter type
  function filterType(sgo: string | undefined): EFilterType {
    switch (Number(sgo)) {
      case 4:
        return EFilterType.ft_slider
      default:
        return EFilterType.ft_checkbox
    }
  }

  // set selected and update psx
  function setSelected(doSet: string | boolean, selected: string[], node?: pf.TPfNode, value?: pf.TPfValue, sKey?: string) {
    const key: string = (node && value) ? [node.nodeName, value.GUID].join('|') : sKey ? sKey : ''
    if (doSet) {
      selected.push(key)
    }
    else {
      selected = selected.filter(item => item !== key)
    }
    setPsx(selected)
  }

  // remove all selected
  function removeAll(selected: string[]) {
    selected = []
    setPsx(selected)
  }

  function setPsx(selected: string[]) {
    if (!maskData.value) { return }

    const psx: string = p.noUrlParams ? (p.psx || '') : (indexStore.state.paramData.queryUrlServer.psx || '')
    let newPsx = ''
    if (selected.length > 0) {
      selected.sort()

      // Get all filters
      const allFilters: pf.TPfNode[] = getAllFilters()
      // Get dependent filter names
      const depFilterNames: string[] = getDepFilterNames(allFilters, selected)

      for (const item of selected) {
        const tAr: string[] = item.split('|')
        const combo: string = tAr[0] || ''
        const value: string = tAr[1] || ''

        let showCombo: boolean = true
        if (depFilterNames) {
          const filter = allFilters.find(item => item.nodeName === combo)
          if (filter && filter.attrs?.FAT) {
            // if a filter is dependent and not in the list skip psx
            if (filter.attrs.FAT === '2') {
              showCombo = depFilterNames.includes(filter.nodeName)
            }
          }
        }

        if (showCombo) {
          // New combo, add combo and value
          if (!(',' + newPsx).includes(',' + combo + '|')) {
            if (newPsx) {
              newPsx = newPsx + ',' + item
            }
            else {
              newPsx = item
            }
            // Add value to existing combo filter
          }
          else {
            newPsx = newPsx + '|' + value
          }
        }
      }
    }
    // only call if psx or param1 has changed
    if (psx !== newPsx) {
      if (p.noUrlParams) {
        emit?.('update:psx', newPsx) // local psx (modal), no navigation
      }
      else {
        ut_navigateNewRoute('', { psx: newPsx }, '', true)
      }
    }
  }

  // Get all filters
  function getAllFilters(): pf.TPfNode[] {
    const res: pf.TPfNode[] = []
    if (!maskData.value) { return res }
    for (const node of maskData.value.nodes) {
      if (node.nodeType === NodeType.nt_SearchFilter) {
        res.push(node)
      }
      else {
        for (const node2 of node.nodes || []) {
          if (node2.nodeType === NodeType.nt_SearchFilter) {
            res.push(node2)
          }
        }
      }
    }
    return res
  }

  // Get dependent filter names from selected
  function getDepFilterNames(allFilters: pf.TPfNode[], selected: string[]): string[] {
    const res: string[] = []
    for (const item of selected) {
      const tAr: string[] = item.split('|')
      const combo: string = tAr[0] || ''
      const value: string = tAr[1] || ''

      const filter = allFilters.find(item => item.nodeName === combo)
      if (!filter) { continue }

      const filtervalue = filter.values?.find(item => item.GUID === value)
      if (!filtervalue) { continue }

      if (filtervalue.attrs) { // ToDo
        if (filtervalue.attrs.FAT2) {
          for (const nodeID of filtervalue.attrs.FAT2.split(',')) {
            const filter = allFilters.find(item => item.nodeID.toString() === nodeID)
            if (filter) {
              res.push(filter.nodeName)
            }
          }
        }
      }
    }
    return res
  }

  const totalCount = computed((): number => {
    return maskData.value?.meta?.total || 0
  })

  return {
    indexStore,
    load,
    maskData,
    totalCount,
    filterType,
    setSelected,
    removeAll,
    setPsx,
    selected: ref(selected),
  }
}
